/**
 * Utility functions for screen monitoring
 */

import ko from "knockout";

/**
 * Helper function to get the connected screen monitoring object from a participant's screenMonitorings array
 * @param {ObservableArray|Array} screenMonitorings - The screenMonitorings array from a participant object
 * @return {Object|null} The connected screen monitoring object with state "connected", or null
 */
const getConnectedScreenMonitoring = function (
    screenMonitorings: any
): any | null {
    // Unwrap in case screenMonitorings is a knockout observableArray
    const unwrappedScreenMonitorings = ko.unwrap(screenMonitorings);
    if (
        !unwrappedScreenMonitorings ||
        !Array.isArray(unwrappedScreenMonitorings) ||
        unwrappedScreenMonitorings.length === 0
    ) {
        return null;
    }
    return (
        unwrappedScreenMonitorings.find(function (item: any) {
            if (!item) {
                return false;
            }
            // Unwrap observable - screenMonitorings items are ObservableModel instances
            // where properties like state are knockout observables
            const state = ko.unwrap(item.state);
            return (
                state === "connected" ||
                (typeof state === "string" &&
                    state.toLowerCase() === "connected")
            );
        }) || null
    );
};

/**
 * Helper function to get targetUser.id from screenMonitorings array where state is connected
 * @param {ObservableArray|Array} screenMonitorings - The screenMonitorings array from a participant object
 * @return {String|null} targetUser.id from connected screen monitoring, or null
 */
const getConnectedTargetUserId = function (
    screenMonitorings: any
): string | null {
    const activeScreenMonitoringSession =
        getConnectedScreenMonitoring(screenMonitorings);
    if (!activeScreenMonitoringSession) {
        return null;
    }
    // Unwrap observables - targetUser and its id may be knockout observables
    const targetUser = ko.unwrap(activeScreenMonitoringSession.targetUser);
    if (!targetUser) {
        return null;
    }
    return ko.unwrap(targetUser.id) ?? null;
};

/**
 * Helper function to get all unique targetUser.id values from screenMonitorings array (regardless of state)
 * @param {ObservableArray|Array} screenMonitorings - The screenMonitorings array from a participant object
 * @return {Array} Array of unique targetUser.id values, or empty array
 */
const getAllUniqueTargetUserIds = function (screenMonitorings: any): string[] {
    // Unwrap in case screenMonitorings is a knockout observableArray
    const unwrappedScreenMonitorings = ko.unwrap(screenMonitorings);
    if (
        !unwrappedScreenMonitorings ||
        !Array.isArray(unwrappedScreenMonitorings) ||
        unwrappedScreenMonitorings.length === 0
    ) {
        return [];
    }
    const targetUserIdsSet = new Set<string>();
    unwrappedScreenMonitorings.forEach(function (item: any) {
        if (!item) {
            return;
        }
        // Unwrap observables - targetUser and its id may be knockout observables
        const targetUser = ko.unwrap(item.targetUser);
        if (targetUser) {
            const targetUserId = ko.unwrap(targetUser.id);
            if (targetUserId) {
                targetUserIdsSet.add(targetUserId);
            }
        }
    });
    return Array.from(targetUserIdsSet);
};

/**
 * Normalizes screen monitoring push notification data from flat structure to array structure.
 * Push notifications may have screenMonitoring (singular) at participant level,
 * but the code expects screenMonitorings (plural array).
 *
 * This function transforms push notification data to match the API response structure:
 * - From: participant.screenMonitoring (object) and participant.targetUser (object)
 * - To: participant.screenMonitorings (array) with nested structure
 *
 * @param  {object} rawData - The raw data from the update notification
 * @return {object} The normalized raw data
 */
const normalizeScreenMonitoringData = function (rawData: any): any {
    // Only normalize if this looks like screen monitoring data
    if (
        !rawData ||
        !rawData.participants ||
        !Array.isArray(rawData.participants)
    ) {
        return rawData;
    }

    // Check if any participant has the flat structure (screenMonitoring singular)
    const needsNormalization = rawData.participants.some(function (
        participant: any
    ) {
        return (
            participant &&
            participant.screenMonitoring &&
            !participant.screenMonitorings
        );
    });

    if (!needsNormalization) {
        return rawData;
    }

    // Clone to avoid mutating the original
    const normalized = JSON.parse(JSON.stringify(rawData));
    normalized.participants = normalized.participants.map(function (
        participant: any
    ) {
        // If participant has flat structure, convert to array structure
        if (
            participant &&
            participant.screenMonitoring &&
            !participant.screenMonitorings
        ) {
            // The id in screenMonitoringItem is the communication ID (screenMonitoringCommunicationId)
            // The screenMonitoring object contains the screen monitoring session ID
            const screenMonitoringItem = {
                id: participant.screenMonitoringCommunicationId,
                state: participant.state,
                targetUser: participant.targetUser,
                screenMonitoring: participant.screenMonitoring,
                monitoringType: participant.monitoringType,
                count: participant.count,
                connectedTime: participant.connectedTime,
                disconnectedTime: participant.disconnectedTime,
            };

            // Create array structure
            participant.screenMonitorings = [screenMonitoringItem];
        }
        return participant;
    });

    return normalized;
};

export default {
    getConnectedScreenMonitoring: getConnectedScreenMonitoring,
    getConnectedTargetUserId: getConnectedTargetUserId,
    getAllUniqueTargetUserIds: getAllUniqueTargetUserIds,
    normalizeScreenMonitoringData: normalizeScreenMonitoringData,
};
